import type { IImage } from '@/interfaces';
import type { ResponsiveImageHints } from '@/services/ResponsiveImageHints';
import type { ImgHTMLAttributes } from 'react';
import { memo } from 'react';
import type { FCl } from 'react-fc';
import styled from 'styled-components';

/**
 * Responsive images are `<img>` elements that contain a series of hints to the browser on which source image to choose.
 * This prevents us from downloading very large images and then squeezing them into a small space. [More info](https://developer.mozilla.org/en-US/docs/Learn/HTML/Multimedia_and_embedding/Responsive_images).

 * Declare the `src` as normal. If it contains `-1x-1` this will be replaced with an appropriate `wxh` value.
 * `atInitial` is required and is the width that this image will be rendered at. This is most commonly a viewport width value, e.g `40vw`.
 * It does not need to be exact, and some components may find it more difficult to judge.
 *
 * For example if the image is going to take up roughly 30% of the viewport use `atInitial="30vw"`. This is only a hint to the browser; this prop doesn't actually set it to 30% of the viewport.
 *
 * The value can be overridden at each breakpoint: `atXS`, `atM`, `atXL` etc.
 *
 * If you inspect the image below you'll notice the "intrinsic size" in dev-tools varies as the viewport width changes, and the network tab shows extra requests for higher resolution images once it gets bigger.
 */
export const ResponsiveImage: FCl<IProps> = memo(({ src, sizeHints, alt = '', className, ...rest }) => {
    const fallbackSrc = typeof src === 'string' ? src : src.baseUrl;
    const styledImgProps = {
        loading: 'lazy',
        src: fallbackSrc,
        alt: alt,
        sizes: sizeHints.getSizes(),
        srcSet: sizeHints.getSrcSet(fallbackSrc),
        ...rest,
    } as ImgHTMLAttributes<HTMLImageElement>;

    if (typeof src === 'string') {
        return <StyledImg className={className} {...styledImgProps} />;
    }

    const ratio: ImgHTMLAttributes<HTMLImageElement> = {};
    if (sizeHints.ratio) {
        // if you know the ratio adding width & height attributes improves rendering performance
        // they don't have to be the right numbers as CSS makes it 100% width, just the ratio between them matters
        ratio.width = 100;
        ratio.height = 100 / sizeHints.ratio;
    }

    return (
        <picture className={className}>
            {src.darkUrl && (
                <source
                    media="(prefers-color-scheme: dark)"
                    sizes={sizeHints.getSizes()}
                    srcSet={sizeHints.getSrcSet(src.darkUrl)}
                />
            )}
            <source
                media="(prefers-color-scheme: light)"
                sizes={sizeHints.getSizes()}
                srcSet={sizeHints.getSrcSet(src.baseUrl)}
            />
            <StyledImg {...ratio} {...styledImgProps} />
        </picture>
    );
});

const StyledImg = styled.img`
    width: 100%;
`;

interface IProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> {
    /** The full URL of the image (original size with -1x-1, which will be replaced) */
    src: string | IImage;

    /** An instance with the hints of what size this image will be at each breakpoint */
    sizeHints: ResponsiveImageHints;
}
